import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "cycle-touring-packing")!;

// Section accents pulled from the brand --gt-feature-* palette, same
// scheme as the nav guides and toll-roads guide.
const ACCENT = {
    teal: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
    rust: { fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" },
    slate: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    green: { fill: "#5b8a72", chipBg: "rgba(91,138,114,.14)", chipText: "#3f6b53" },
    plum: { fill: "#7a4f6d", chipBg: "rgba(122,79,109,.14)", chipText: "#5f3c54" },
} as const;

type Item = { title: string; body: string };
type Section = {
    catnum: string;
    chipLabel: string;
    accent: keyof typeof ACCENT;
    heading: string;
    intro?: string;
    items: Item[];
};

const SECTIONS: Section[] = [
    {
        catnum: "01",
        chipLabel: "Get these right or it ends at the border",
        accent: "teal",
        heading: "Documents — get these right or the trip ends at the border",
        intro:
            "Cycling needs less paper than motoring, but post-Brexit you still need more than a passport. Sort it two weeks out, not the night before.",
        items: [
            {
                title: "Passport",
                body:
                    "Must be valid for at least 3 months after the day you plan to leave the Schengen area, and issued within the last 10 years on the date you enter. The Schengen 90-in-180 rule applies — count carefully if you've been over recently.",
            },
            {
                title: "Travel insurance — check the cycling small print",
                body:
                    "Many standard policies cap 'cycling' at leisure speeds and exclude touring or anything in a sportive. Yellow Jersey, ETA and BUPA Worldwide cover proper multi-day touring including medical repatriation. Confirm cover for the bike itself, not just you.",
            },
            {
                title: "GHIC card",
                body:
                    "Replaces the old EHIC for state healthcare in the EU and Switzerland. It is NOT a substitute for travel insurance — A&E in a Swiss valley town will still bill you, just less brutally.",
            },
            {
                title: "Bike insurance",
                body:
                    "Travel insurance often excludes the bike. A dedicated cycle policy (Yellow Jersey, Cycleplan, ETA) covers theft, accidental damage, and third-party liability — the last is mandatory by law in Switzerland for cyclists, usually bundled into household insurance for residents.",
            },
            {
                title: "Breakdown cover",
                body:
                    "RAC and AA don't cover bicycles. ETA Cycle Rescue and Cycling UK both offer pan-European recovery for £30–60/year — bike to the nearest station, train to the next town, your call. Worth it on a remote Alpine pass.",
            },
            {
                title: "Photocopies",
                body:
                    "Photo every document on your phone, plus a paper copy stowed in a different pannier from the originals. Add an emergency contact card with blood group and any allergies.",
            },
        ],
    },
    {
        catnum: "02",
        chipLabel: "Pack for the worst hour",
        accent: "rust",
        heading: "Riding kit — clothing",
        intro:
            "Alpine descents in the rain, 35°C valley afternoons, then a 5°C col summit — all in the same day. Pack for the worst hour and you'll be fine.",
        items: [
            {
                title: "Helmet",
                body:
                    "MIPS or equivalent rotational-impact system if your budget stretches. Replace if more than 5 years old or after any meaningful impact. A road-style aero helmet vents better on long climbs than a skate-style lid; on a tour it earns its keep.",
            },
            {
                title: "Padded bib shorts × 2 or 3",
                body:
                    "The single most important kit choice. Assos Mille GT, Castelli Free, or Rapha Core are the proven workhorses. Bring at least two pairs so one can dry overnight. Replace yours BEFORE the tour if the pad is older than two years — a tired pad ruins a tour faster than rain.",
            },
            {
                title: "Jerseys and base layers",
                body:
                    "Two short-sleeve jerseys with rear pockets. A thin merino base layer for cool starts and chilly summits — merino doesn't stink after three days of wear, synthetic does. Pack one long-sleeve jersey or arm warmers for early starts.",
            },
            {
                title: "Lightweight rain jacket",
                body:
                    "Goretex Active or Shakedry; packable to fit a jersey pocket. Galibier Mistral and Castelli Squadra are the value picks. Skip cheap PVC ponchos — they don't survive an Alpine downpour at 60 km/h.",
            },
            {
                title: "Gloves",
                body:
                    "Mitts for daily use and full-finger gloves for cool mornings and descents. Your hands carry weight all day on a loaded bike — gel pads in the palm are worth the £10 upgrade.",
            },
            {
                title: "Cycling shoes",
                body:
                    "If you ride clipless, bring SPD MTB shoes rather than road shoes — you can actually walk in them around hotels and cobbled town centres. Spare cleats and the right Allen key are worth packing.",
            },
            {
                title: "Off-bike kit",
                body:
                    "One change of lightweight non-cycling clothes (T-shirt, shorts, lightweight sandals) for evenings. Resist the urge to bring more — every extra t-shirt is a kilogram up the Stelvio.",
            },
            {
                title: "Eyewear",
                body:
                    "Cycling glasses with interchangeable lenses (clear, low-light, tinted). Italian and Swiss tunnels are pitch-dark coming off bright valley sunshine — clear lenses there are a safety issue, not a fashion one.",
            },
        ],
    },
    {
        catnum: "03",
        chipLabel: "Test it loaded before you go",
        accent: "slate",
        heading: "The bike itself",
        intro:
            "Test the loaded setup at home BEFORE the tour. A bike that feels fine empty handles entirely differently with 12kg of luggage on the back.",
        items: [
            {
                title: "Tyres",
                body:
                    "28–35mm minimum for cross-border touring on a mix of tarmac and rough chip-seal. Schwalbe Marathon Plus is the bombproof standard; Continental GP 5000 if you want speed and accept the puncture risk. Carry two spare tubes even if you're tubeless — sealant can't fix a sidewall cut.",
            },
            {
                title: "Saddle",
                body:
                    "Use the saddle you've ridden 500+ km on. The tour is not the time to break in a Brooks B17 — that takes about 1,000 km. SQlab, fizik Aliante and Specialized Power are the most-recommended off-the-shelf touring saddles.",
            },
            {
                title: "Gearing",
                body:
                    "For the Alps loaded, you want a 1:1 ratio or lower. Compact 50/34 with an 11-32 cassette is the minimum; 1× with a 42T chainring and 11-50 cassette is better. The Stelvio's east side is a sustained 9% — gears save your knees, not your fitness.",
            },
            {
                title: "Bell",
                body:
                    "Mandatory in Germany and Switzerland. A €5 brass ping-bell does the job. Useful in any country for warning Alpine hikers on shared paths.",
            },
            {
                title: "Lights — legal AND visibility",
                body:
                    "Front white at least 100 lumens, rear red at least 30 lumens, both with a steady mode (some countries fine you for flashing-only). Knog Blinder Mob 30/Mob 75, Cateye Volt 200, or a Busch+Müller dynamo light if your hub is set up. Carry spare batteries or a USB cable.",
            },
            {
                title: "Luggage",
                body:
                    "Ortlieb Back Roller Classic panniers (rear) are the touring default — waterproof, indestructible, repairable. A handlebar bag (Ortlieb Ultimate, Apidura) for camera, snacks and documents. For lighter setups: a frame bag plus saddle pack (bikepacking style) keeps weight central but reduces capacity to about a long weekend's kit.",
            },
            {
                title: "Reflectives",
                body:
                    "Tyres with reflective sidewall stripes (Schwalbe Marathon range) plus pedal reflectors are required in Germany and a good idea everywhere. A small reflective sticker on each pannier passes you through a Carabinieri spot-check.",
            },
        ],
    },
    {
        catnum: "04",
        chipLabel: "Turn a repair into a non-event",
        accent: "green",
        heading: "Tools, spares, and roadside fixes",
        intro:
            "Most days on tour something will be slightly off — a creak, a rubbing brake, a missed shift. Carrying the basics turns a 10-minute repair into something you don't even mention that evening.",
        items: [
            {
                title: "Spare inner tubes × 2",
                body:
                    "Even if you're tubeless. A sidewall slash that sealant can't seal needs a tube. Match valve length to your rim depth — a 40mm valve won't reach through 50mm-deep rims.",
            },
            {
                title: "Patch kit and tyre boot",
                body:
                    "A glueless patch kit (Park Tool GP-2) plus a few self-adhesive tyre boots (Park Tool TB-2 or a piece of an old tyre sidewall) for slashes too big to seal.",
            },
            {
                title: "Mini-pump or CO2",
                body:
                    "Mini-pump is more reliable for repeated punctures and works in any temperature; CO2 is faster but you'll run out. The Lezyne Pressure Drive and the Topeak Race Rocket are the two reliable picks. If you carry CO2, pack three cartridges minimum.",
            },
            {
                title: "Multi-tool with chain breaker",
                body:
                    "Crankbrothers M19 or Topeak Mini 20 Pro. Must include 2, 2.5, 3, 4, 5, 6mm hex; Torx T25 (modern disc rotors); a chain breaker and quick-link tool.",
            },
            {
                title: "Quick-link, derailleur hanger, spoke key",
                body:
                    "Two chain quick-links matched to your chain (10/11/12-speed). A spare rear derailleur hanger SPECIFIC to your frame — universal ones rarely fit. A 3-size spoke key for emergency truing of a tacoed wheel.",
            },
            {
                title: "Chain lube",
                body:
                    "100ml of wet lube (Squirt, Smoove or Rock'n'Roll) and a small rag. Re-lube every refuel stop in the wet. Dry-lube is faster but useless after one Alpine downpour.",
            },
            {
                title: "Cable ties, electrical tape, super-glue",
                body:
                    "A dozen heavy-duty zip ties, half a roll of electrical tape, a sachet of cyanoacrylate. Field-fix for a broken pannier strap, a snapped shoe buckle or a torn saddle cover.",
            },
        ],
    },
    {
        catnum: "05",
        chipLabel: "Route, power, staying found",
        accent: "plum",
        heading: "Navigation and tech",
        items: [
            {
                title: "Komoot or RideWithGPS",
                body:
                    "Both have offline maps, cycling-specific routing (avoids autoroutes, prefers cycle paths), and turn-by-turn voice prompts via Bluetooth headphones or your bike computer. Download every leg + 30km either side BEFORE leaving the UK.",
            },
            {
                title: "Bike computer (optional but worth it)",
                body:
                    "Garmin Edge 540 or Wahoo Elemnt Bolt give you a 12-hour ride time on a single charge, see in direct sunlight, and survive rain. Your phone alone works but kills the battery and overheats on the bars.",
            },
            {
                title: "Phone as backup",
                body:
                    "With Google Maps and Maps.me downloaded offline. A waterproof phone mount (Quad Lock with a vibration damper, or a top-tube bag with a transparent map window).",
            },
            {
                title: "Power",
                body:
                    "20,000 mAh power bank or a dynamo hub with a Sinewave Beacon / Forumslader USB-C charger. The dynamo solution is heavier but charges-as-you-ride for indefinite range. USB-C cable plus the right wall adapter — UK plugs don't fit anywhere else.",
            },
            {
                title: "Bell, lights, reflective ankle bands",
                body:
                    "Lights covered in 'the bike itself'. Reflective ankle bands (Salzmann, Wowow) are cheap and put movement at car-driver eye level — they massively improve roundabout visibility.",
            },
        ],
    },
    {
        catnum: "06",
        chipLabel: "Stay comfortable, fed and solvent",
        accent: "teal",
        heading: "Comfort, money and health",
        items: [
            {
                title: "Chamois cream",
                body:
                    "Apply every morning, no exceptions. Assos Chamois Cream, Doublebase or the cheaper Muc-Off all work. A 200ml tub does a two-week tour with two riders. Saddle sores end tours.",
            },
            {
                title: "Sunscreen and lip balm",
                body:
                    "Factor 50 on the back of the neck, ears, and the V of your jersey at the collarbone. Alpine UV at 2,000m strips a layer of skin per day. Lip balm with SPF — your lips chap fast at altitude.",
            },
            {
                title: "Hydration and electrolytes",
                body:
                    "Two 750ml bottles minimum on the bike. Electrolyte tabs (SiS Hydro, Precision Hydration, Nuun) for any day over 30°C or with 1,500m+ of climbing. Hyponatraemia is a real risk on long Alpine days — water alone isn't enough.",
            },
            {
                title: "Food on the bike",
                body:
                    "Real food in the bar bag (banana, croissant, ham roll), gels for emergencies only. Most French boulangeries are between 12:00 and 14:30; bring snacks in if you're riding through lunch. Italian Conad and Coop supermarkets are reliable refuel stops.",
            },
            {
                title: "Cards and cash",
                body:
                    "A no-fee travel card (Wise, Revolut, Chase) for everyday spend. Carry a backup debit card stored in a different pannier. €100 cash for small mountain refuges plus CHF 50–100 for Switzerland — alpine huts sometimes don't take cards.",
            },
            {
                title: "Medications and small first-aid",
                body:
                    "Personal prescriptions in original packaging plus copy of the prescription. A compact kit: paracetamol, ibuprofen, antihistamines, plasters, blister pads, saline wash, sterile gauze, anti-diarrhoea, rehydration sachets, and a tick remover for rural Germany and southern France.",
            },
        ],
    },
    {
        catnum: "07",
        chipLabel: "The pre-flight that saves the tour",
        accent: "rust",
        heading: "The day before — final checks",
        items: [
            {
                title: "Tyres",
                body:
                    "Inflated to the loaded value (heavier rider + 12kg luggage means closer to the max printed on the sidewall than the spec sheet). Check for embedded grit and sidewall cracks — sidewalls are how most touring tyres fail.",
            },
            {
                title: "Chain wear",
                body:
                    "Park Tool CC-3.2 chain checker. If it drops in at the 0.75 mark, fit a new chain BEFORE you leave. A stretched chain wears the cassette and chainrings four times faster — and seizing on the Stelvio is not a story you want to tell.",
            },
            {
                title: "Brakes",
                body:
                    "Pad thickness (replace if under 1.5mm for discs, under 2mm for rim). Cable tension on mechanical brakes; lever feel on hydraulic. Alpine descents will eat 1–2mm of pad per day of riding.",
            },
            {
                title: "Gears",
                body:
                    "Indexed cleanly through every gear with no skipping or hesitation. Eyeball the rear derailleur hanger — if it's bent even 2mm, the gears will hunt all tour.",
            },
            {
                title: "Bolts and torque",
                body:
                    "Stem, handlebar, seatpost, rack mounts, bottle cages, cleats — every bolt to spec with a torque key. Most touring mechanicals are bolts working loose, not catastrophic failures.",
            },
            {
                title: "Wheels",
                body:
                    "Spin each wheel — listen for rubbing, watch for wobble. A wheel that's marginally out of true at home will be properly out after a week of pannier weight.",
            },
            {
                title: "Loaded test ride",
                body:
                    "Pack the bike as you'll ride it, do a 30km loop including a steep hill. Anything that creaks, rubs or feels wrong gets fixed at home — not in a French Pyrenean village on a Sunday.",
            },
        ],
    },
];

const COUNTRY_RULES = [
    {
        flag: "🇫🇷",
        name: "France",
        body:
            "Hi-viz vest is MANDATORY outside built-up areas at night or in poor visibility — it must be worn, not just carried. Front white and rear red lights required after dark. Helmet is mandatory for under-12s (riders and passengers); strongly recommended for everyone but not enforced for adults. Cyclists are banned from autoroutes and some routes nationales — check signage carefully.",
    },
    {
        flag: "🇩🇪",
        name: "Germany",
        body:
            "A working bell is LEGALLY required (StVZO §64a). So are working front + rear lights, plus reflectors on the pedals, wheels and seat post. Helmets are not legally required but most German cyclists wear them. Where a cycle path (Radweg) is signposted, you must use it — riding on the road alongside attracts a fine. Hi-viz isn't mandatory but is a very good idea on rural roads.",
    },
    {
        flag: "🇨🇭",
        name: "Switzerland",
        body:
            "Lights required after dark and in tunnels (any tunnel — even a 50m one). The old Velovignette / annual sticker was abolished in 2012 and replaced with mandatory third-party liability insurance for cyclists; check yours covers Switzerland. Helmets are recommended for under-16s but not legally required. Like in France, cyclists are banned from motorways. Cycling under the influence is enforced as strictly as driving — the limit is 0.5g/l.",
    },
    {
        flag: "🇮🇹",
        name: "Italy",
        body:
            "Hi-viz vest MUST be worn outside built-up areas after sunset and in any tunnel (also during daytime). Front white and rear red lights required at all times after sunset. Bell is mandatory. Helmets are recommended, not required, except in some local zones. Cyclists are banned from autostrade — use SS or SP routes. Drink-driving rules apply to cyclists.",
    },
];

function PackingGuideCycling() {
    usePageMeta({
        title:
            "What to pack for a cycle tour in France, Germany, Switzerland and Italy — Planamoto",
        description:
            "A cyclist's checklist for cross-border European touring. Documents, country-specific cycling rules, kit, the bike itself, tools, navigation, comfort and the day-before checks for France, Germany, Switzerland and Italy.",
        url: "https://www.planamoto.com/guides/cycle-touring-packing",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="What to pack for a cycle tour through France, Germany, Switzerland and Italy"
            lede="Cross-border cycle touring in Europe is one of the great rides — but each country has its own legal kit list, and a small oversight can leave you fined, walking, or stuck. This is the kit list we'd take on a two-week loop of the Alps and the Black Forest by bike. Don't take everything; do check every section."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Loaded touring bicycle with panniers ready for a cross-border ride",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        eyebrow="What I never leave without"
                        authorNote="Spencer · on the touring bike"
                        heading="The three that make or break a tour"
                        intro="Bring everything below and you'll be dragging weight up the Stelvio; skip the wrong thing and you're fined, saddle-sore or stranded. These three I sort before anything else — the full list follows."
                        columns={[
                            {
                                label: "01 · Bib shorts ×3",
                                title: "The contact-point kit",
                                body: "Assos, Castelli or Rapha, replaced if the pad's over two years old. A tired pad ruins a tour faster than rain.",
                            },
                            {
                                label: "02 · Legal kit",
                                title: "Bell, lights, liability",
                                body: "A working bell (required in Germany and Switzerland), steady front-white and rear-red lights, third-party liability cover for Switzerland.",
                            },
                            {
                                label: "03 · Chamois cream",
                                title: "Applied every morning",
                                body: "No exceptions. A 200ml tub does a two-week tour for two riders. Saddle sores end tours.",
                            },
                        ]}
                        backstop="a loaded test ride at home before you go, and Planamoto's printable checklist ticked off as it's packed. Anything that creaks gets fixed on your street, not a French village on a Sunday."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full kit list
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's the top three. Here's everything, section by section.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Everything worth carrying, grouped the way you'd load the bike
                            — with the legal must-haves flagged, not buried. Don't take
                            all of it; do check every section.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideSection
                index={1}
                catnum={SECTIONS[0].catnum}
                heading={SECTIONS[0].heading}
                intro={SECTIONS[0].intro}
                chipLabel={SECTIONS[0].chipLabel}
                accent={ACCENT[SECTIONS[0].accent]}
                items={SECTIONS[0].items.map((it) => ({ ...it, marker: "✓" }))}
            />
            <GuideSection
                index={2}
                heading="Country-specific cycling rules you must follow"
                intro="Cycling laws vary more than you'd think. Helmets, bells and lights are the headline differences — getting them right avoids a roadside fine and avoids the 'tourist who thought rules didn't apply' label."
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={COUNTRY_RULES.map((c) => ({
                    title: c.name,
                    body: c.body,
                    marker: c.flag,
                }))}
            />
            {SECTIONS.slice(1).map((s, i) => (
                <GuideSection
                    key={s.heading}
                    index={i + 3}
                    catnum={s.catnum}
                    heading={s.heading}
                    intro={s.intro}
                    chipLabel={s.chipLabel}
                    accent={ACCENT[s.accent]}
                    items={s.items.map((it) => ({ ...it, marker: "✓" }))}
                />
            ))}

            <GuideCtaBand
                heading="We handle the plan and the checklist. You handle the panniers."
                body="Build the route with elevation and food stops, split the expenses across currencies, and run a shared packing checklist the whole group ticks off before you roll out. Clone a hand-curated alpine tour as your starting point."
            />

            <RelatedGuides
                slugs={["packing-europe", "power-plugs", "fuel-prices"]}
            />
            <CtaCard
                title="Plan the route too"
                body="Once the kit is sorted, the route is the fun part. Browse our hand-curated multi-day tours and clone one as your starting point."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default PackingGuideCycling;
